/**
 * ContextMenu.tsx — 锚定按钮的轻量动作菜单弹层
 *
 * 移植自原型 design/Novel-admin/src/contextmenu.jsx：点击 children 触发器在其锚点处
 * 打开一个 fixed 定位的 popover，渲染 items（分隔线 '---' / 分组标题 {section} / 动作项）。
 * 用 React.cloneElement 给 children 注入 ref + onClick 切换开关；菜单经 createPortal 挂到 document.body。
 * 具备原生菜单语义、键盘循环导航和可预测的关闭后焦点策略。
 */

import {
  type CSSProperties,
  Fragment,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
  cloneElement,
  createElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '~/lib/icons';
import {
  collectDocumentTabbableElements,
  useFocusScope,
  useOwnedFocusScopePortalRef,
} from './focus-scope';

/** 菜单项：分隔线 / 分组标题 / 可点击动作项 */
export type ContextMenuItem =
  | '---'
  | { section: string }
  | {
      label: string;
      icon?: keyof typeof Icons;
      onClick?: () => void;
      tone?: 'danger';
      disabled?: boolean;
      shortcut?: string;
      /** 悬停说明（如 disabled 时解释禁用原因：NOT-in-scope / 权限不足）*/
      title?: string;
    };

interface ContextMenuProps {
  items: ContextMenuItem[];
  children: ReactElement;
  align?: 'left' | 'right';
  width?: number;
  /**
   * 触发器没有自身可访问名称时使用；已知 Icons-only 按钮默认使用“更多操作”。
   * 自定义组件的渲染内容不可静态检查，纯图标自定义组件必须显式传入此属性，缺省时不会伪造名称。
   */
  triggerLabel?: string;
}

/** popover 弹层定位信息（fixed 坐标系） */
interface PopoverPos {
  top: number;
  left: number;
  width: number;
}

type CloseFocusStrategy = 'none' | 'trigger' | 'trigger-if-unclaimed';

const KNOWN_ICON_COMPONENT_TYPES = new Set<unknown>(Object.values(Icons));

function hasMeaningfulTriggerContent(content: ReactNode): boolean {
  if (typeof content === 'string' || typeof content === 'number' || typeof content === 'bigint') {
    return /[\p{L}\p{N}]/u.test(String(content));
  }
  if (
    isValidElement<{
      children?: ReactNode;
      hidden?: boolean;
      'aria-hidden'?: boolean | string;
      style?: CSSProperties;
    }>(content)
  ) {
    const isInlineHidden =
      content.props.style?.display === 'none' ||
      content.props.style?.visibility === 'hidden' ||
      content.props.style?.visibility === 'collapse';
    if (content.props.hidden || String(content.props['aria-hidden']) === 'true' || isInlineHidden) {
      return false;
    }
    if (content.type === Fragment || typeof content.type === 'string') {
      return hasMeaningfulTriggerContent(content.props.children);
    }
    if (KNOWN_ICON_COMPONENT_TYPES.has(content.type)) return false;
    // 无法在不执行组件的前提下判断自定义组件是否渲染文字；不覆盖其潜在原生可访问名称。
    return true;
  }
  if (Array.isArray(content)) return content.some(hasMeaningfulTriggerContent);
  if (
    content !== null &&
    typeof content === 'object' &&
    Symbol.iterator in content &&
    typeof content[Symbol.iterator] === 'function'
  ) {
    return Array.from(content as Iterable<ReactNode>).some(hasMeaningfulTriggerContent);
  }
  return false;
}

export function ContextMenu({
  items,
  children,
  align = 'right',
  width = 200,
  triggerLabel,
}: ContextMenuProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const [activeActionIndex, setActiveActionIndex] = useState(-1);
  const menuId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const focusScope = useFocusScope();
  const ownedPortalRef = useOwnedFocusScopePortalRef(triggerRef, popRef);
  const actionRefs = useRef(new Map<number, HTMLButtonElement>());
  const focusedOnOpenRef = useRef(false);
  const closeFocusStrategyRef = useRef<CloseFocusStrategy>('none');
  const pendingFocusAnimationFrameRef = useRef<number | null>(null);

  const actionIndexes = items.flatMap((item, index) =>
    item !== '---' && !('section' in item) ? [index] : [],
  );

  const closeMenu = (focusStrategy: CloseFocusStrategy) => {
    closeFocusStrategyRef.current = focusStrategy;
    setOpen(false);
  };

  const findTabTargetRelativeToTrigger = (moveBackward: boolean): HTMLElement | null => {
    const triggerElement = triggerRef.current;
    if (!triggerElement) return null;
    const tabbableElements = focusScope
      ? focusScope.collectTabbableElements(popRef.current)
      : collectDocumentTabbableElements(popRef.current);
    const triggerIndex = tabbableElements.indexOf(triggerElement);
    if (triggerIndex < 0) return triggerElement;
    const adjacentIndex = triggerIndex + (moveBackward ? -1 : 1);
    if (!focusScope) return tabbableElements[adjacentIndex] ?? triggerElement;
    const wrappedIndex = (adjacentIndex + tabbableElements.length) % tabbableElements.length;
    return tabbableElements[wrappedIndex] ?? triggerElement;
  };

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const margin = 8;
      let left = align === 'right' ? r.right - width : r.left;
      if (left + width > window.innerWidth - margin) left = window.innerWidth - width - margin;
      if (left < margin) left = margin;
      let top = r.bottom + 4;
      // 28px per item + 12 padding; clamp
      const guessH = items.filter((it) => it !== '---').length * 30 + 16;
      if (top + guessH > window.innerHeight - margin) top = Math.max(margin, r.top - guessH - 4);
      setPos({ top, left, width });
    };
    update();
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      closeFocusStrategyRef.current = 'none';
      setOpen(false);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open, align, width, items]);

  useEffect(() => {
    if (!open || !pos || focusedOnOpenRef.current) return;
    focusedOnOpenRef.current = true;
    const firstActionIndex = actionIndexes[0] ?? -1;
    setActiveActionIndex(firstActionIndex);
    (actionRefs.current.get(firstActionIndex) ?? popRef.current)?.focus();
  }, [actionIndexes, open, pos]);

  useEffect(() => {
    if (open || closeFocusStrategyRef.current === 'none') return;
    const focusStrategy = closeFocusStrategyRef.current;
    closeFocusStrategyRef.current = 'none';
    const triggerElement = triggerRef.current;
    pendingFocusAnimationFrameRef.current = requestAnimationFrame(() => {
      pendingFocusAnimationFrameRef.current = null;
      if (!triggerElement?.isConnected) return;
      const activeElement = document.activeElement;
      const externalElementOwnsFocus =
        activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        activeElement !== document.documentElement &&
        activeElement.isConnected;
      if (focusStrategy === 'trigger' || !externalElementOwnsFocus) triggerElement.focus();
    });
    return () => {
      if (pendingFocusAnimationFrameRef.current !== null) {
        cancelAnimationFrame(pendingFocusAnimationFrameRef.current);
        pendingFocusAnimationFrameRef.current = null;
      }
    };
  }, [open]);

  const focusAction = (index: number) => {
    setActiveActionIndex(index);
    actionRefs.current.get(index)?.focus();
  };

  const handleMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeMenu('trigger');
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      const tabTarget = findTabTargetRelativeToTrigger(e.shiftKey) ?? triggerRef.current;
      closeMenu('none');
      tabTarget?.focus();
      return;
    }
    if (actionIndexes.length === 0) return;

    const focusedIndex = actionIndexes.find(
      (index) => actionRefs.current.get(index) === document.activeElement,
    );
    const currentIndex = focusedIndex ?? activeActionIndex;
    const position = actionIndexes.indexOf(currentIndex);

    if (e.key === 'Home') {
      e.preventDefault();
      const firstIndex = actionIndexes[0];
      if (firstIndex !== undefined) focusAction(firstIndex);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      const lastIndex = actionIndexes[actionIndexes.length - 1];
      if (lastIndex !== undefined) focusAction(lastIndex);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const nextPosition =
        (Math.max(position, 0) + step + actionIndexes.length) % actionIndexes.length;
      const nextIndex = actionIndexes[nextPosition];
      if (nextIndex !== undefined) focusAction(nextIndex);
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && focusedIndex !== undefined) {
      e.preventDefault();
      actionRefs.current.get(focusedIndex)?.click();
    }
  };

  // 给触发器注入 ref + onClick；保留 children 原有 onClick，再切换菜单开关
  const childProps = children.props as {
    onClick?: (e: ReactMouseEvent) => void;
    children?: unknown;
    title?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
  };
  const triggerHasMeaningfulText = hasMeaningfulTriggerContent(childProps.children as ReactNode);
  const triggerAriaLabel =
    childProps['aria-label'] ??
    triggerLabel ??
    childProps.title ??
    (childProps['aria-labelledby'] || triggerHasMeaningfulText ? undefined : '更多操作');
  const trigger = cloneElement(children, {
    ref: triggerRef as Ref<HTMLElement>,
    'aria-label': triggerAriaLabel,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': menuId,
    onClick: (e: ReactMouseEvent) => {
      e.stopPropagation();
      childProps.onClick?.(e);
      if (!open) {
        if (pendingFocusAnimationFrameRef.current !== null) {
          cancelAnimationFrame(pendingFocusAnimationFrameRef.current);
          pendingFocusAnimationFrameRef.current = null;
        }
        focusedOnOpenRef.current = false;
        closeFocusStrategyRef.current = 'none';
        setActiveActionIndex(actionIndexes[0] ?? -1);
      } else {
        closeFocusStrategyRef.current = 'none';
      }
      setOpen(!open);
    },
  } as Partial<typeof children.props>);

  return (
    <>
      {trigger}
      {open &&
        pos &&
        createPortal(
          <div
            ref={ownedPortalRef}
            id={menuId}
            role="menu"
            aria-label={triggerAriaLabel ?? triggerLabel ?? childProps.title ?? '操作菜单'}
            tabIndex={-1}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              zIndex: 200,
              padding: 4,
              animation: 'fade-in 0.1s ease',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleMenuKeyDown}
          >
            {items.map((it, i) => {
              if (it === '---')
                return (
                  <div
                    key={i}
                    role="separator"
                    aria-orientation="horizontal"
                    style={{ height: 1, background: 'var(--border)', margin: '4px 6px' }}
                  />
                );
              if ('section' in it)
                return (
                  <div
                    key={i}
                    role="presentation"
                    aria-hidden="true"
                    style={{
                      padding: '8px 10px 4px',
                      fontSize: 10,
                      color: 'var(--fg-faint)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontWeight: 500,
                    }}
                  >
                    {it.section}
                  </div>
                );
              const danger = it.tone === 'danger';
              const itemStyle: CSSProperties = {
                padding: '6px 10px',
                borderRadius: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12.5,
                cursor: it.disabled ? 'not-allowed' : 'pointer',
                color: danger ? 'var(--neg)' : it.disabled ? 'var(--fg-faint)' : 'var(--fg)',
                opacity: it.disabled ? 0.6 : 1,
                width: '100%',
                border: 0,
                background: 'transparent',
                fontFamily: 'inherit',
                lineHeight: 'inherit',
                textAlign: 'left',
              };
              return (
                <div key={i} role="presentation" title={it.title}>
                  <button
                    ref={(node) => {
                      if (node) actionRefs.current.set(i, node);
                      else actionRefs.current.delete(i);
                    }}
                    type="button"
                    role="menuitem"
                    aria-disabled={it.disabled || undefined}
                    tabIndex={activeActionIndex === i ? 0 : -1}
                    title={it.title}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (it.disabled) return;
                      // Fire action FIRST so dialogs mount before the menu unmounts
                      // and before any trailing mouseup → click events can hit a
                      // freshly-mounted backdrop.
                      try {
                        it.onClick?.();
                      } catch (err) {
                        console.error('ContextMenu action failed:', err);
                      } finally {
                        closeMenu('trigger-if-unclaimed');
                      }
                    }}
                    onFocus={() => setActiveActionIndex(i)}
                    style={itemStyle}
                    onMouseEnter={(e) => {
                      if (!it.disabled)
                        e.currentTarget.style.background = danger
                          ? 'var(--neg-soft)'
                          : 'var(--bg-subtle)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {it.icon && createElement(Icons[it.icon], { size: 12 })}
                    <span style={{ flex: 1 }}>{it.label}</span>
                    {it.shortcut && (
                      <span
                        style={{
                          fontSize: 10,
                          color: 'var(--fg-faint)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {it.shortcut}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
